/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import {
  FiBriefcase,
  FiCamera,
  FiCheck,
  FiClock,
  FiEdit,
  FiEdit2,
  FiFileText,
  FiMail,
  FiMapPin,
  FiPhone,
  FiPlus,
  FiUser,
  FiX,
} from "react-icons/fi";
import { LuCrown } from "react-icons/lu";
import api from "../../../configs/api";
import CompanyModal from "../../modals/CompanyModal";
import uploadFileToCloudinary from "../../../utils/uploadFileToCloudinary";
import { showError, showSuccess } from "../../../utils/toastUtils";
import { capitalize, cleanArray } from "../../../utils/helpers";
import ProfileTabSkeleton from "../skeleton-loaders/profile-subscription/ProfileTabSkeleton";
import { CustomDropdown } from "../common/form-elements";

const SummaryCard = ({ userData }) => {
  const memberSince = userData?.createdAt
    ? new Date(userData.createdAt).toLocaleDateString("en-US", {
        month: "short",
        year: "numeric",
      })
    : "-";

  const lastLogin = userData?.lastLogin
    ? new Date(userData.lastLogin).toLocaleDateString("en-US", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "No recent activity";

  const accountType = capitalize(userData?.role) || "User";

  const subscriptionPlan = userData?.subscription || "Free";

  const isVerified = userData?.isVerified;

  return (
    <div className="space-y-4">
      {/* ACCOUNT SUMMARY */}
      <div className="bg-white rounded-lg shadow-sm p-5">
        <h3 className="text-lg font-bold text-gray-900 mb-4">
          Account Summary
        </h3>

        <div className="space-y-4">
          {[
            ["Member Since", memberSince],
            ["Last Login", lastLogin],
            ["Account Type", accountType],
            [
              "Verification",
              isVerified ? (
                <span className="text-green-600 font-semibold">Verified</span>
              ) : (
                <span className="text-red-500 font-semibold">Not Verified</span>
              ),
            ],
            ["Subscription Plan", subscriptionPlan],
          ].map(([label, value], idx) => (
            <div className="flex items-center justify-between" key={idx}>
              <span className="text-sm text-gray-600">{label}</span>
              <span className="text-sm font-semibold text-gray-900">
                {value}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* UPGRADE CARD */}
      <div className="bg-primary hover:bg-primary/90 rounded-lg shadow-sm p-5 text-white">
        <LuCrown className="w-8 h-8 mb-3" />

        <h3 className="text-lg font-bold mb-2">Upgrade to Enterprise</h3>

        <p className="text-sm text-white/90 mb-4">
          Get unlimited listings and premium features
        </p>

        <button
          type="button"
          className="w-full bg-white text-blue-600 font-semibold py-2 rounded-lg hover:bg-gray-100 transition-colors"
        >
          Learn More
        </button>
      </div>
    </div>
  );
};


const BaseProfileFields = ({ isEditing, register, errors }) => {
  const activeInput =
    "border-gray-300 bg-white focus:ring-1 focus:ring-primary focus:outline-none";
  const inactiveInput = "border-gray-200 bg-gray-50 text-text-secondary";

  return (
    <div className="space-y-3 sm:space-y-5">
      <h3 className="text-lg font-semibold text-text-primary">
        Personal Information
      </h3>

      {/* Full Name */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Full Name
        </label>
        <div className="relative">
          <FiUser
            className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted"
            size={18}
          />
          <input
            {...register("fullName", { required: "Full name is required" })}
            disabled={!isEditing}
            className={`w-full pl-11 pr-4 py-3 border text-sm sm:text-base rounded-lg transition-all ${
              isEditing ? activeInput : inactiveInput
            }`}
          />
        </div>

        {errors.fullName && (
          <p className="text-status-danger text-xs mt-1">
            {errors.fullName.message}
          </p>
        )}
      </div>

      {/* Email + Phone */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Email
          </label>
          <div className="relative">
            <FiMail
              className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted"
              size={18}
            />
            <input
              {...register("email", { required: "Email is required" })}
              disabled={!isEditing}
              className={`w-full pl-11 pr-4 py-3 text-sm sm:text-base border rounded-lg transition-all ${
                isEditing ? activeInput : inactiveInput
              }`}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-text-primary mb-2">
            Phone Number
          </label>
          <div className="relative">
            <FiPhone
              className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted"
              size={18}
            />
            <input
              {...register("phone", { required: "Phone is required" })}
              disabled={!isEditing}
              className={`w-full pl-11 pr-4 py-3 text-sm sm:text-base border rounded-lg transition-all ${
                isEditing ? activeInput : inactiveInput
              }`}
            />
          </div>
        </div>
      </div>

      {/* Preferred City */}
      <div>
        <label className="block text-sm font-medium text-text-primary mb-2">
          Preferred City
        </label>
        <div className="relative">
          <FiMapPin
            className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted"
            size={18}
          />
          <input
            {...register("preferredCity")}
            disabled={!isEditing}
            className={`w-full pl-11 pr-4 py-3 text-sm sm:text-base border rounded-lg transition-all ${
              isEditing ? activeInput : inactiveInput
            }`}
          />
        </div>
      </div>
    </div>
  );
};

const AgentBuilderFields = ({
  register,
  isEditing,
  worksWithCompany,
  setWorksWithCompany,
  watch,
  setValue,
  companies,
  fetchCompanies,
}) => {
  const [isCompanyModalOpen, setIsCompanyModalOpen] = useState(false);
  const [companyModalMode, setCompanyModalMode] = useState("create"); // "create" | "edit"
  const [selectedCompany, setSelectedCompany] = useState(null);
  const selectedCompanyId = watch("companyId");

  useEffect(() => {
    if (!companies || companies.length === 0) return;
    if (!selectedCompanyId) return;

    const found = companies.find((c) => c._id === selectedCompanyId);
    setSelectedCompany(found || null);
  }, [companies, selectedCompanyId]);

  const activeInput =
    "border-gray-300 bg-white focus:ring-1 focus:ring-primary focus:outline-none";
  const inactiveInput =
    "border-gray-200 bg-gray-50 text-text-secondary text-sm sm:text-base";

  const handleModalSuccess = (company) => {
    setSelectedCompany(company);
    fetchCompanies();
  };

  return (
    <>
      <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 border-t border-border">
        <div className="space-y-3 sm:space-y-5">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              RERA ID
            </label>
            <div className="relative">
              <FiFileText
                className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted"
                size={18}
              />
              <input
                {...register("reraId")}
                disabled={!isEditing}
                className={`w-full pl-11 pr-4 py-3 border rounded-lg transition-all ${
                  isEditing ? activeInput : inactiveInput
                }`}
              />
            </div>
          </div>

          {/* Operating Since */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Operating Since
              <span className="text-text-muted text-xs ml-2">(Year)</span>
            </label>
            <div className="relative">
              <FiClock
                className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted"
                size={18}
              />
              <input
                type="number"
                {...register("operatingSince")}
                disabled={!isEditing}
                className={`w-full pl-11 pr-4 py-3 border rounded-lg transition-all ${
                  isEditing ? activeInput : inactiveInput
                }`}
              />
            </div>
          </div>

          {/* Operating Areas */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Operating Areas
              <span className="text-text-muted text-xs ml-2">
                (comma separated)
              </span>
            </label>
            <textarea
              {...register("operatingAreas")}
              disabled={!isEditing}
              rows={2}
              placeholder="Patia, Nayapalli, Cuttack Road..."
              className={`w-full px-4 py-3 border rounded-lg resize-none transition-all ${
                isEditing ? activeInput : inactiveInput
              }`}
            />
          </div>

          {/* Deal Types */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Deal Types
              <span className="text-text-muted text-xs ml-2">
                (comma separated)
              </span>
            </label>
            <textarea
              {...register("dealTypes")}
              disabled={!isEditing}
              rows={2}
              placeholder="Resale, Original Booking..."
              className={`w-full px-4 py-3 border rounded-lg resize-none transition-all ${
                isEditing ? activeInput : inactiveInput
              }`}
            />
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
            {[
              { label: "Properties for sale", name: "propertiesForSale" },
              { label: "Deal Closed", name: "dealClosed" },
              { label: "Team Members (optional)", name: "teamMembers" },
            ].map((item) => (
              <div key={item.name}>
                <label className="block text-sm font-medium text-text-secondary mb-2">
                  {item.label}
                </label>
                <input
                  type="number"
                  {...register(item.name)}
                  disabled={!isEditing}
                  className={`w-full px-4 py-3 border rounded-lg transition-all ${
                    isEditing ? activeInput : inactiveInput
                  }`}
                />
              </div>
            ))}
          </div>

          {/* WORK STATUS */}
          <div className="mb-6 p-4  bg-background rounded-lg border border-border">
            <div className="flex flex-col gap-2 sm:flex-row items-start sm:items-center justify-between cursor-pointer">
              <div className="flex sm:flex-col md:flex-row items-center sm:items-start w-full justify-start md:items-center gap-1 sm:gap-2 md:gap-3 text-sm sm:text-base my-2">
                <p className="font-medium  text-text-primary">Work Status:</p>
                <p className="text-sm text-text-secondary ">
                  {worksWithCompany
                    ? "Working with a company"
                    : "Operating independently"}
                </p>
              </div>

              <div className="flex items-center gap-3 md:self-center">
                <span
                  className={`text-sm font-medium ${
                    !worksWithCompany ? "text-primary" : "text-text-secondary"
                  }`}
                >
                  Independent
                </span>

                <button
                  type="button"
                  disabled={!isEditing}
                  onClick={() => {
                    setWorksWithCompany(!worksWithCompany);
                    setSelectedCompany(null);
                    setValue("companyId", null);
                  }}
                  className={`relative w-14 h-7 rounded-full transition-colors ${
                    worksWithCompany ? "bg-primary" : "bg-gray-300"
                  } ${!isEditing && "opacity-50 cursor-not-allowed"}`}
                >
                  <span
                    className={`absolute top-1 left-1 w-5 h-5 bg-white rounded-full transition-transform ${
                      worksWithCompany ? "translate-x-7" : ""
                    }`}
                  />
                </button>

                <span
                  className={`text-sm font-medium ${
                    worksWithCompany ? "text-primary" : "text-text-secondary"
                  }`}
                >
                  Company
                </span>
              </div>
            </div>
          </div>

          {/* COMPANY SELECT + DETAILS + MODAL TRIGGER */}
          {worksWithCompany && (
            <div className="mb-6 p-5 bg-background rounded-lg border border-border">
              <label className="block text-sm font-medium text-text-primary mb-2">
                Select Company
              </label>

              <CustomDropdown
                value={watch("companyId")}
                disabled={!isEditing || isCompanyModalOpen}
                onChange={(val) => {
                  setValue("companyId", val);
                  const selected = companies.find((c) => c._id === val);
                  setSelectedCompany(selected || null);
                }}
                options={companies.map((c) => ({
                  value: c._id,
                  logo: c.companyLogo,
                  label: c.companyName,
                }))}
                placeholder="Choose existing company..."
                className={isEditing ? activeInput : inactiveInput}
              />

              {/* Selected company preview (read-only) */}
              {selectedCompany && (
                <div className="mt-4 p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden">
                      {selectedCompany.companyLogo ? (
                        <img
                          src={selectedCompany.companyLogo}
                          className="w-full h-full object-cover"
                          alt="Company logo"
                        />
                      ) : (
                        <FiBriefcase className="text-gray-400 text-xl" />
                      )}
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-text-primary mb-1">
                        {selectedCompany.companyName}
                      </p>
                      {selectedCompany.reraId && (
                        <p className="text-xs text-text-secondary">
                          RERA: {selectedCompany.reraId}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div className="my-3 text-sm font-medium text-center">OR</div>

              {/* Open modal: create or edit */}
              <button
                type="button"
                disabled={!isEditing}
                onClick={() => {
                  if (selectedCompany) {
                    setCompanyModalMode("edit");
                  } else {
                    setCompanyModalMode("create");
                  }
                  setIsCompanyModalOpen(true);
                }}
                className="text-sm font-medium text-primary hover:text-text-primary cursor-pointer flex items-center gap-1"
              >
                <FiEdit className="w-4 h-4" />
                {selectedCompany
                  ? "Edit Selected Company"
                  : "Create New Company"}
              </button>
              {selectedCompany && (
                <button
                  type="button"
                  disabled={!isEditing}
                  onClick={() => {
                    setCompanyModalMode("create");
                    setIsCompanyModalOpen(true);
                  }}
                  className="mt-2 text-sm font-medium text-primary hover:text-text-primary cursor-pointer flex items-center gap-1"
                >
                  <FiPlus className="w-4 h-4" />
                  Create New Company
                </button>
              )}

              {/* Modal itself */}
              <CompanyModal
                open={isCompanyModalOpen}
                mode={companyModalMode}
                initialData={
                  companyModalMode === "edit" ? selectedCompany : null
                }
                onClose={() => setIsCompanyModalOpen(false)}
                onSuccess={handleModalSuccess}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const AvatarUpload = ({
  isEditing,
  value,
  defaultValue = "",
  firstLetter,
  onChange,
}) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  let preview = value || defaultValue || "";

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const localURL = URL.createObjectURL(file);
    onChange(localURL);

    setUploading(true);
    try {
      const { uploadPromise } = uploadFileToCloudinary(file);
      const result = await uploadPromise;

      if (!result || !result.url) {
        onChange(defaultValue || "");
        return;
      }

      onChange(result.url);
    } catch (error) {
      const msg =
        typeof error === "string"
          ? error
          : error?.message
          ? error.message
          : "Upload failed";

      showError(msg);
      onChange(defaultValue || "");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div
      onClick={() => isEditing && inputRef.current?.click()}
      className={`relative w-16 h-16 sm:w-20 sm:h-20 flex-shrink-0 rounded-full overflow-hidden group transition 
        ${isEditing ? "cursor-pointer" : "cursor-default"}`}
    >
      {preview ? (
        <img
          src={preview}
          className="w-full h-full object-cover"
          alt="Profile"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-primary to-primary-navy flex items-center justify-center text-white text-2xl font-semibold">
          {firstLetter}
        </div>
      )}

      {uploading && (
        <div className="absolute inset-0 flex items-center justify-center bg-white/60 rounded-lg">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
        </div>
      )}

      {isEditing && (
        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
          <FiCamera className="text-white text-2xl" />
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFile}
      />
    </div>
  );
};

const ProfileTab = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [worksWithCompany, setWorksWithCompany] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm();

  // const selectedCompanyId = watch("companyId");
  const profileImage = watch("profileImage");
  useEffect(() => {
    fetchProfileData();
  }, []);

  const fetchProfileData = async () => {
    setIsLoading(true);

    try {
      const res = await api.get(`/users/my-profile`);
      const profileData = res?.data.profile;
      // console.log("Profile data:", profileData);

      setUserData(profileData);
      if (profileData?.user.role === "agent-builder") {
        setWorksWithCompany(Boolean(profileData.agentBuilder?.companyId));
        fetchCompanies();
        reset({
          profileImage: profileData.user.profileImage,
          fullName: profileData.user.fullName,
          email: profileData.user.email,
          phone: profileData.user.phone,
          preferredCity: profileData.user.preferredCity,

          reraId: profileData.agentBuilder.reraId,
          operatingSince: profileData.agentBuilder.operatingSince,
          operatingAreas: profileData.agentBuilder.operatingAreas?.join(", "),
          dealTypes: profileData.agentBuilder.dealTypes?.join(", "),
          propertiesForSale: profileData.agentBuilder.propertiesForSale,
          dealClosed: profileData.agentBuilder.dealClosed,
          teamMembers: profileData.agentBuilder.teamMembers,
          companyId: profileData.agentBuilder.companyId,
        });
      } else {
        reset({
          profileImage: profileData.user.profileImage,
          fullName: profileData.user.fullName,
          email: profileData.user.email,
          phone: profileData.user.phone,
          preferredCity: profileData.user.preferredCity,
        });
      }

      setIsLoading(false);
    } catch (error) {
      console.log("error fetching profile:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const res = await api.get(`/companies`);
      setCompanies(res.data?.companies);
    } catch (error) {
      console.error("Failed to fetch companies", error);
    }
  };

  const transformPayload = (data) => {
    const operatingAreasArray = data.operatingAreas
      ? cleanArray(data.operatingAreas.split(","))
      : [];

    const dealTypesArray = data.dealTypes
      ? cleanArray(data.dealTypes.split(","))
      : [];
    return {
      user: {
        profileImage: data.profileImage,
        fullName: data.fullName,
        email: data.email,
        phone: data.phone,
        preferredCity: data.preferredCity,
      },

      agentBuilder: {
        reraId: data.reraId,
        operatingSince: data.operatingSince,
        operatingAreas: operatingAreasArray,
        dealTypes: dealTypesArray,
        propertiesForSale: Number(data.propertiesForSale),
        dealClosed: Number(data.dealClosed),
        teamMembers: Number(data.teamMembers),
        companyId: data.companyId || null,
      },
    };
  };

  const onSubmit = async (data) => {
    const payload = transformPayload(data);
    // console.log("FINAL PAYLOAD:", payload);
    try {
      await api.put("/users/update-me", payload);
      // console.log("resp:", resp.data);
      showSuccess("Profile Updated Successfully");
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating profile,", error);
    }
  };

  if (isLoading) return <ProfileTabSkeleton />;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="grid grid-cols-1 lg:grid-cols-3 gap-4 "
    >
      {/* LEFT SIDE */}
      <div className="lg:col-span-2 bg-white rounded-xl shadow-sm p-4  sm:p-6">
        {/* AVATAR */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between border-b border-border mb-4 pb-6">
          <div className="flex items-center gap-3 sm:gap-5">
            <div className="flex-shrink-0">
              <AvatarUpload
                isEditing={isEditing}
                value={profileImage}
                firstLetter={(userData?.user?.fullName || "U")
                  .charAt(0)
                  .toUpperCase()}
                defaultValue={userData?.user?.profileImage}
                onChange={(url) => setValue("profileImage", url)}
              />
            </div>

            <div>
              <h3 className="text-lg font-semibold text-text-primary">
                {userData?.user?.fullName}
              </h3>
              <p className="text-text-secondary text-sm">
                {userData?.user?.email}
              </p>
            </div>
          </div>

          <div className="flex flex-row -mb-2 sm:mb-0 items-end sm:items-center self-end sm:self-start gap-1.5 sm:gap-3 text-sm">
            {!isEditing ? (
              <button
                type="button"
                onClick={() => setIsEditing(true)}
                className="flex text-xs sm:text-base items-center justify-center gap-1 sm:gap-2 bg-primary text-white px-4 py-1.5 sm:py-2 rounded-lg hover:bg-primary/90 transition-colors font-medium min-w-[4rem] sm:min-w-0"
              >
                <FiEdit2 size={14} />
                <span className="hidden sm:block">Edit</span>
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => {
                    setIsEditing(false);
                    reset();
                  }}
                  className="flex text-xs sm:text-base items-center gap-1 sm:gap-2 border justify-center border-gray-300 text-gray-700 px-4 py-1.5
                   sm:py-2 rounded-lg hover:bg-red-500 hover:text-white transition-colors font-medium min-w-[3rem] sm:min-w-0"
                >
                  <FiX size={14} />
                  <span className="hidden sm:block">Cancel</span>
                </button>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex items-center text-xs sm:text-base justify-center  gap-1 sm:gap-2 bg-status-success text-white  px-4 py-1.5 sm:py-2 rounded-lg hover:bg-status-success/90 transition-colors font-medium disabled:opacity-50 min-w-[4rem] sm:min-w-0"
                >
                  <FiCheck className="size-4" />
                  <span className="hidden sm:block">
                    {isSubmitting ? "Saving..." : "Save"}
                  </span>
                </button>
              </>
            )}
          </div>
        </div>

        {/* BASE PROFILE */}
        <BaseProfileFields
          isEditing={isEditing}
          register={register}
          errors={errors}
        />
        {/* AGENT BUILDER */}
        {userData?.user.role === "agent-builder" && (
          <AgentBuilderFields
            register={register}
            isEditing={isEditing}
            worksWithCompany={worksWithCompany}
            setWorksWithCompany={setWorksWithCompany}
            watch={watch}
            setValue={setValue}
            companies={companies}
            fetchCompanies={fetchCompanies}
          />
        )}
      </div>

      {/* SUMMARY CARD */}
      <SummaryCard userData={userData?.user} />
    </form>
  );
};

export default ProfileTab;
